import { useState, useRef, useId } from "react"
import axios from "axios"
import { useUploadDocument, useDeleteDocument, useDocumentStatus } from "../hooks/useDocuments"
import type { CourseDocument, DocumentStatus } from "../types"
import { useTranslation } from "react-i18next"

const ACCEPTED_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "text/plain",
]
const ACCEPTED_EXTS = ".pdf,.docx,.doc,.txt"
const MAX_BYTES = 20 * 1024 * 1024
// Le dépôt multi-fichiers (input `multiple`) est le flux normal du produit :
// un élève dépose souvent plusieurs PDF d'un coup. Plafonner les requêtes
// concurrentes évite de saturer le réseau/l'UI de progression sans jamais
// bloquer artificiellement un dépôt groupé réaliste (la limite qui compte
// reste le throttle serveur, 60 requêtes/minute).
const MAX_CONCURRENT_UPLOADS = 3

interface LocalUpload {
  localId: string
  fileName: string
  progress: number
  status: "uploading" | "done" | "error"
  errorMsg?: string
}

interface DocumentStatusRowProps {
  courseId: string
  doc: CourseDocument
  onDelete: (docId: string) => void
  isDeleting: boolean
}

const STATUS_META: Record<DocumentStatus, { labelKey: string; className: string }> = {
  pending: { labelKey: "courses.upload.status_pending", className: "text-neutral-600" },
  processing: { labelKey: "courses.upload.status_processing", className: "text-primary-500" },
  ready: { labelKey: "courses.upload.status_ready", className: "text-mindeia-success" },
  failed: { labelKey: "courses.upload.status_failed", className: "text-mindeia-error" },
}

function DocumentStatusRow({ courseId, doc, onDelete, isDeleting }: DocumentStatusRowProps) {
  const { t } = useTranslation()
  const { data: statusData } = useDocumentStatus(courseId, doc.id)
  const currentStatus: DocumentStatus = statusData?.status ?? doc.status
  const meta = STATUS_META[currentStatus]

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-surface-200 bg-white px-4 py-3">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
          className="flex-shrink-0 text-neutral-600"
        >
          <path
            d="M5 2h8l4 4v12a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z"
            stroke="currentColor"
            strokeWidth="1.4"
            fill="none"
          />
          <path d="M13 2v5h4" stroke="currentColor" strokeWidth="1.4" />
        </svg>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-neutral-900">{doc.originalName}</p>
          <p className={["text-xs", meta.className].join(" ")}>{t(meta.labelKey)}</p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => onDelete(doc.id)}
        disabled={isDeleting}
        aria-label={t('courses.upload.delete_document', { name: doc.originalName })}
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-neutral-600 transition-colors hover:bg-mindeia-error-light hover:text-mindeia-error focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-error disabled:cursor-not-allowed disabled:opacity-50"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path
            d="M1 3h12M5 3V2a1 1 0 011-1h2a1 1 0 011 1v1M2 3l1 9a1 1 0 001 1h6a1 1 0 001-1l1-9"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  )
}

interface DocumentUploadZoneProps {
  courseId: string
  documents: CourseDocument[]
}

export function DocumentUploadZone({ courseId, documents }: DocumentUploadZoneProps) {
  const { t } = useTranslation()
  const inputId = useId()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [localUploads, setLocalUploads] = useState<LocalUpload[]>([])

  const uploadMutation = useUploadDocument(courseId)
  const deleteMutation = useDeleteDocument(courseId)

  function validateFile(file: File): string | null {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return "Format non supporté. Acceptés : PDF, DOCX, DOC, TXT."
    }
    if (file.size > MAX_BYTES) {
      return "Fichier trop volumineux. Maximum : 20 Mo."
    }
    return null
  }

  async function uploadFile(file: File) {
    const validationErr = validateFile(file)
    const localId = `${Date.now()}-${Math.random().toString(36).slice(2)}`

    if (validationErr) {
      setLocalUploads((prev) => [
        ...prev,
        { localId, fileName: file.name, progress: 0, status: "error", errorMsg: validationErr },
      ])
      return
    }

    setLocalUploads((prev) => [
      ...prev,
      { localId, fileName: file.name, progress: 0, status: "uploading" },
    ])

    try {
      await uploadMutation.mutateAsync({
        file,
        onProgress: (pct) =>
          setLocalUploads((prev) =>
            prev.map((u) => (u.localId === localId ? { ...u, progress: pct } : u))
          ),
      })
      setLocalUploads((prev) =>
        prev.map((u) => (u.localId === localId ? { ...u, status: "done", progress: 100 } : u))
      )
    } catch (err) {
      const isRateLimited = axios.isAxiosError(err) && err.response?.status === 429
      setLocalUploads((prev) =>
        prev.map((u) =>
          u.localId === localId
            ? {
                ...u,
                status: "error",
                errorMsg: t(
                  isRateLimited
                    ? "courses.upload.rate_limited"
                    : "courses.upload.upload_error"
                ),
              }
            : u
        )
      )
    }
  }

  function handleFiles(files: FileList | File[]) {
    const queue = Array.from(files)

    async function worker() {
      let next = queue.shift()
      while (next) {
        await uploadFile(next)
        next = queue.shift()
      }
    }

    // File d'attente à concurrence plafonnée : un fichier = une requête, mais
    // au plus MAX_CONCURRENT_UPLOADS en vol à la fois, jamais toutes en
    // parallèle sans plafond.
    const workerCount = Math.min(MAX_CONCURRENT_UPLOADS, queue.length)
    for (let i = 0; i < workerCount; i++) {
      void worker()
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragOver(false)
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
    }
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragOver(true)
  }

  function onDragLeave() {
    setIsDragOver(false)
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Drop zone */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        role="button"
        tabIndex={0}
        aria-label={t("courses.upload.dropzone_label")}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click()
        }}
        className={[
          "flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary",
          isDragOver
            ? "border-mindeia-primary bg-mindeia-primary-light"
            : "border-surface-200 bg-surface-50 hover:border-mindeia-primary",
        ].join(" ")}
      >
        <svg
          width="36"
          height="36"
          viewBox="0 0 36 36"
          fill="none"
          aria-hidden="true"
          className="text-neutral-600"
        >
          <path
            d="M18 24V12M18 12l-5 5M18 12l5 5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect
            x="4"
            y="4"
            width="28"
            height="28"
            rx="6"
            stroke="currentColor"
            strokeWidth="1.4"
            fill="none"
          />
        </svg>
        <div>
          <p className="text-sm font-medium text-neutral-800">
            {t("courses.upload.drop_hint")}{" "}
            <span className="text-mindeia-primary underline">
              {t("courses.upload.browse")}
            </span>
          </p>
          <p className="mt-1 text-xs text-neutral-600">{t("courses.upload.constraints")}</p>
        </div>
        <input
          ref={fileInputRef}
          id={inputId}
          type="file"
          accept={ACCEPTED_EXTS}
          multiple
          className="sr-only"
          aria-hidden="true"
          onChange={(e) => {
            if (e.target.files) {
              handleFiles(e.target.files)
              e.target.value = ""
            }
          }}
        />
      </div>

      {/* Local upload progress rows */}
      {localUploads.filter((u) => u.status !== "done").length > 0 && (
        <div className="flex flex-col gap-2">
          {localUploads
            .filter((u) => u.status !== "done")
            .map((u) => (
              <div
                key={u.localId}
                className="flex flex-col gap-1.5 rounded-lg border border-surface-200 bg-white px-4 py-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-sm font-medium text-neutral-900">{u.fileName}</p>
                  {u.status === "error" ? (
                    <span className="flex-shrink-0 text-xs text-mindeia-error">
                      {u.errorMsg ?? t("courses.upload.error")}
                    </span>
                  ) : (
                    <span className="flex-shrink-0 text-xs text-neutral-600">
                      {u.progress}%
                    </span>
                  )}
                </div>
                {u.status === "uploading" && (
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-100">
                    <div
                      className="h-full rounded-full bg-mindeia-primary transition-all"
                      style={{ width: `${u.progress}%` }}
                    />
                  </div>
                )}
              </div>
            ))}
        </div>
      )}

      {/* Existing documents */}
      {documents.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-surface-800">
            Documents ({documents.length})
          </p>
          {documents.map((doc) => (
            <DocumentStatusRow
              key={doc.id}
              courseId={courseId}
              doc={doc}
              onDelete={(docId) => deleteMutation.mutate(docId)}
              isDeleting={deleteMutation.isPending}
            />
          ))}
        </div>
      )}

      {documents.length === 0 &&
        localUploads.filter((u) => u.status !== "error").length === 0 && (
          <p className="text-center text-sm text-neutral-600">
            {t('courses.no_documents')}
          </p>
        )}
    </div>
  )
}
